import React, { useEffect, useState } from 'react'
import { Plus, Edit } from 'lucide-react'
import { Button, Input, Form, message, Checkbox, Select, Radio, TimePicker } from 'antd'
import { useSelector } from 'react-redux'
import { translations } from '../../utils/constants'
import { secureApi } from '../../Config/api'
import { POST } from '../../utils/apis'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { convertItalianToLocalTime, convertLocalToItalianTime } from '../../utils/helpers'
import moment from 'moment'
import useUserLocation from '../../Hooks/useUserLocation'

dayjs.extend(customParseFormat)

const { Option } = Select

const LogEntry = ({ language, onSuccess, onCancel, isEdit = false, editData = null }) => {
  const { refetchLocation } = useUserLocation()
  const user = useSelector(state => state.authReducer?.user)
  const role = user?.role
  const userId = user?.id
  const employeeName = user?.userName
  const employeeNameList = useSelector(state => state.employeeLogReducer.nameList)
  const employeeData = useSelector((state) => state.employeeLogReducer.data)
  const [form] = Form.useForm()
  const [lunchBreak, setLunchBreak] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [timeOption, setTimeOption] = useState('system')
  const [selectedEmployeeLog, setSelectedEmployeeLog] = useState(null)
  const [manualTime, setManualTime] = useState(null)
  const [startTime, setStartTime] = useState(null)
  const [endTime, setEndTime] = useState(null)
  const [employeeTodayLog, setEmployeeTodayLog] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const entryTime = employeeTodayLog?.entry_time
    ? convertItalianToLocalTime(employeeTodayLog.entry_time, "HH:mm:ss")
    : null;

  const disabledBeforeEntry = (current) => {
    if (!entryTime) return false; // allow all if no entry time
    return current && current.isBefore(entryTime, "minute");
  };

  useEffect(() => {
    if (["admin", "teamTrack", "superAdmin"].includes(user?.role)) {
      setEmployeeTodayLog({
        id: user?.id,
        user_name: user?.userName,
      })
    }
  }, [user])

  // console.log('employeeTodayLog', employeeTodayLog)

  useEffect(() => {
    const today = moment().startOf("day"); // today's date (00:00)
    let employeeFilteredLog = employeeData?.filter((obj) =>
      obj?.user_id === selectedEmployee?.id &&
      moment(obj.work_day).isSame(today, "day")
    )
    if (employeeFilteredLog?.length) {
      setEmployeeTodayLog(employeeFilteredLog?.[0])
    }
  }, [selectedEmployee])


  useEffect(() => {
    if (isEdit && editData) {
      form.setFieldsValue({
        lunch: editData.lunch,
        dinner: editData.dinner,
        hotel: editData.hotel_expense,
        ferry_expense: editData.ferry_expense,
        telepass: editData.telepass,
        extra: editData.extra,
        kilometers: editData.km_traveled,
        kmRate: editData.km_rate,
        travelExpenses: editData.travel_expenses
      })
      setLunchBreak(!!editData.lunch_break)

      if (role === 'admin') {
        const emp = employeeNameList.find(e => e.user_name === editData.employee_name)
        setSelectedEmployee(emp || null)
      }

      if (editData.entry_time) {
        const localStart = convertItalianToLocalTime(editData.entry_time)
        setStartTime(dayjs(localStart.format("HH:mm"), "HH:mm"))
      }

      if (editData.exit_time) {
        const localEnd = convertItalianToLocalTime(editData.exit_time)
        setEndTime(dayjs(localEnd.format("HH:mm"), "HH:mm"))
      }
    }
  }, [isEdit, editData, employeeNameList, role, form])

  const onFinish = async (values) => {
    setSubmitting(true)
    try {
      const { ip, lat, lon } = await refetchLocation();

      let finalUserId = userId
      let finalEmployeeName = employeeName
      if (["admin", "teamTrack", "superAdmin"].includes(role) && selectedEmployee) {
        finalUserId = selectedEmployee.id
        finalEmployeeName = selectedEmployee.user_name
      }

      const italyDate = moment().tz("Europe/Rome").format("YYYY-MM-DD")

      let response

      if (isEdit) {
        const startItalian = startTime
          ? convertLocalToItalianTime(moment(startTime.toDate())).format("HH:mm:ss")
          : ""
        const endItalian = endTime
          ? convertLocalToItalianTime(moment(endTime.toDate())).format("HH:mm:ss")
          : ""

        const editPayload = {
          id: editData.id,
          ...values,
          user_id: finalUserId,
          employeeName: finalEmployeeName,
          lunchBreak,
          workDate: italyDate,
          startTime: startItalian,
          endTime: endItalian,
        }

        response = await secureApi.post(POST?.UPDATE_EMPLOYEE_LOG, editPayload)
        console.log('editPayload', editPayload)
      } else {
        const endTimeItalian =
          timeOption === "system"
            ? convertLocalToItalianTime(moment()).format("HH:mm:ss")
            : manualTime
              ? convertLocalToItalianTime(moment(manualTime, "HH:mm")).format("HH:mm:ss")
              : ""

        const addPayload = {
          ...values,
          user_id: finalUserId,
          employeeName: finalEmployeeName,
          lunchBreak,
          workDate: italyDate,
          endTime: endTimeItalian,
          ip, lat, lon
        }


        response = await secureApi.post(POST.ADD_EMPLOYEE_LOG, addPayload)
      }

      if (response?.success) {
        message.success(
          isEdit
            ? "Log updated successfully!"
            : "Log entry submitted successfully!"
        )
        form.resetFields()
        setLunchBreak(false)
        setTimeOption("system")
        setManualTime(null)
        setStartTime(null)
        setEndTime(null)
        if (typeof onSuccess === "function") onSuccess()
      } else {
        message.error(response?.message || "Failed to submit log entry")
      }
    } catch (err) {
      message.error("Error occurred while submitting")
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setLunchBreak(false)
    setTimeOption('system')
    setManualTime(null)
    setStartTime(null)
    setEndTime(null)
    if (typeof onCancel === 'function') onCancel()
  }

  return (
    <div className="timesheet-container">
      <div className="timesheet-wrapper">
        <div className="timesheet-grid form-card">
          <Form form={form} onFinish={onFinish} layout="vertical" initialValues={{ kmRate: '0.35' }}>
            {["admin", "teamTrack", "superAdmin"].includes(role) && (
              <>
                <div className="form-group">
                  <h2 className="section-header">{translations[language]?.PersonalInfo}</h2>

                  <Form.Item label={translations[language]?.selectEmployees} required>
                    <Select
                      showSearch
                      placeholder={translations[language]?.selectEmployees}
                      size="large"
                      value={selectedEmployee?.user_name || user?.userName}
                      disabled={isEdit}
                      onChange={(val) => {
                        const emp = employeeNameList.find(e => e.user_name === val)
                        if (emp) {
                          setSelectedEmployee(emp)
                        } else {
                          setSelectedEmployee({
                            id: user?.id,
                            user_name: val
                          })
                        }
                      }}
                    >
                      {role === "admin" && (
                        <Option key="me" value={user?.userName}>
                          Me
                        </Option>
                      )}

                      {employeeNameList?.map((emp) => (
                        <Option key={emp.id} value={emp.user_name}>
                          {emp.user_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {isEdit ? (
                    <>
                      <p style={{ marginBottom: 8 }}>{translations[language].selectStartTime}</p>
                      {/* <TimePicker
                        value={startTime}
                        onChange={setStartTime}
                        format="HH:mm"
                        style={{ width: '100%', marginBottom: 16 }}
                      /> */}
                      <TimePicker
                        value={startTime}
                        onChange={setStartTime}
                        format="HH:mm"
                        placeholder={translations[language]?.selectTime}
                        style={{ width: '100%', marginBottom: 16, borderRadius: 6, height: 40 }}
                        disabledTime={() => {
                          const now = new Date();
                          const currentHour = now.getHours();
                          const currentMinute = now.getMinutes();

                          return {
                            disabledHours: () => {
                              const hours = [];
                              for (let i = currentHour + 1; i < 24; i++) {
                                hours.push(i);
                              }
                              return hours;
                            },

                            disabledMinutes: (selectedHour) => {
                              if (selectedHour === currentHour) {
                                const minutes = [];
                                for (let i = currentMinute + 1; i < 60; i++) {
                                  minutes.push(i);
                                }
                                return minutes;
                              }
                              return [];
                            }
                          };
                        }}
                      />

                      <p style={{ marginBottom: 8 }}>{translations[language].SelectExit}</p>
                      <TimePicker
                        value={endTime}
                        onChange={setEndTime}
                        format="HH:mm"
                        placeholder={translations[language]?.selectTime}
                        style={{ width: '100%', marginBottom: 16, borderRadius: 6, height: 40 }}
                        disabledTime={() => {
                          if (!editData?.entry_time) return {}
                          const entryMoment = convertItalianToLocalTime(editData.entry_time)
                          const entryHour = entryMoment.hour()
                          const entryMinute = entryMoment.minute()

                          return {
                            disabledHours: () => {
                              const hours = []
                              for (let i = 0; i < entryHour; i++) hours.push(i)
                              return hours
                            },
                            disabledMinutes: (selectedHour) => {
                              if (selectedHour === entryHour) {
                                const minutes = []
                                for (let i = 0; i < entryMinute; i++) minutes.push(i)
                                return minutes
                              }
                              return []
                            }
                          }
                        }}
                      />

                    </>
                  ) : (
                    <>
                      {entryTime && <p>Entry Time is :{entryTime?.format('HH:mm')}</p>}
                      <Radio.Group
                        style={{ marginBottom: 16, display: 'flex', gap: 16 }}
                        value={timeOption}
                        onChange={(e) => setTimeOption(e.target.value)}
                      >
                        <Radio value="system">{translations[language]?.currentTime}</Radio>
                        <Radio value="manual">{translations[language].enterManualTime}</Radio>
                      </Radio.Group>

                      {timeOption === 'manual' && (
                        <>
                          <p style={{ marginBottom: 8 }}>{translations[language].SelectExit}</p>
                          {/* same entry time  and exit time  allowed in this ↓*/}
                          <TimePicker
                            value={manualTime}
                            onChange={setManualTime}
                            format="HH:mm"
                            placeholder={translations[language]?.selectTime}
                            style={{ width: "100%", marginBottom: 16, borderRadius: 6, height: 40 }}
                            inputReadOnly
                            disabledTime={() => {
                              if (!entryTime) return {};
                              const disabledHours = Array.from({ length: entryTime.hour() }, (_, i) => i);
                              const disabledMinutes = (selectedHour) => {
                                if (selectedHour === entryTime.hour()) {
                                  return Array.from({ length: entryTime.minute() }, (_, i) => i);
                                }
                                return [];
                              };

                              return {
                                disabledHours: () => disabledHours,
                                disabledMinutes,
                              };
                            }}
                          />

                          {/* same entry time  and exit time not allowed in this ↓*/}
                          {/* <TimePicker
                            value={manualTime}
                            onChange={setManualTime}
                            format="HH:mm"
                            style={{ width: "100%", marginBottom: 16 }}
                            inputReadOnly
                            disabledTime={() => {
                              if (!entryTime) return {};

                              const entryHour = entryTime.hour();
                              const entryMinute = entryTime.minute();

                              // Disable all hours before entry hour
                              const disabledHours = Array.from({ length: entryHour }, (_, i) => i);

                              const disabledMinutes = (selectedHour) => {
                                if (selectedHour < entryHour) {
                                  return Array.from({ length: 60 }, (_, i) => i); // all minutes disabled
                                }
                                if (selectedHour === entryHour) {
                                  // Disable minutes up to and including entry minute
                                  return Array.from({ length: entryMinute + 1 }, (_, i) => i);
                                }
                                return [];
                              };

                              return {
                                disabledHours: () => disabledHours,
                                disabledMinutes,
                              };
                            }}
                          /> */}

                        </>
                      )}
                    </>
                  )}
                </div>
                <div className="separator" />
              </>
            )}

            {/* <div className="form-group">
              <Form.Item>
                <h2 className="section-header">{translations[language]?.WorkingHours}</h2>
                <Checkbox
                  checked={lunchBreak}
                  onChange={(e) => setLunchBreak(e.target.checked)}
                >
                  {translations[language]?.lunchBreakInfo}
                </Checkbox>
              </Form.Item>
            </div>

            <div className="separator" /> */}
            <div className="form-group">
              <h2 className="section-header">{translations[language]?.ExtraExpenses}</h2>

              <div className="form-row two-cols">
                <Form.Item label={translations[language]?.Lunch} name="lunch">
                  <Input style={{ borderRadius: 6 }} type="number" step="0.01" size="large" placeholder={translations[language]?.amount} min={0} />
                </Form.Item>
                <Form.Item label={translations[language]?.Dinner} name="dinner">
                  <Input style={{ borderRadius: 6 }} type="number" step="0.01" size="large" placeholder={translations[language]?.amount} min={0} />
                </Form.Item>
              </div>

              <div className="form-row two-cols">
                <Form.Item label={translations[language]?.HotelExpense} name="hotel">
                  <Input style={{ borderRadius: 6 }} type="number" step="0.01" size="large" placeholder={translations[language]?.amount} min={0} />
                </Form.Item>
                <Form.Item label={translations[language]?.Ferry} name="ferry_expense">
                  <Input style={{ borderRadius: 6 }} type="number" step="0.01" size="large" placeholder={translations[language]?.amount} min={0} />
                </Form.Item>
              </div>

              <div className="form-row two-cols">
                <Form.Item label={translations[language]?.Telepass} name="telepass">
                  <Input style={{ borderRadius: 6 }} type="number" step="0.01" size="large" placeholder={translations[language]?.amount} min={0} />
                </Form.Item>
                <Form.Item label={translations[language]?.Extra} name="extra">
                  <Input style={{ borderRadius: 6 }} type="number" step="0.01" size="large" placeholder={translations[language]?.amount} min={0} />
                </Form.Item>
              </div>

              <div className="form-row two-cols">
                <Form.Item label={translations[language]?.KMTraveled} name="kilometers">
                  <Input style={{ borderRadius: 6 }} type="number" step="0.01" size="large" placeholder={translations[language]?.distanceInKm} min={0} />
                </Form.Item>
                <Form.Item label={translations[language]?.KMRate} name="kmRate">
                  <Input style={{ borderRadius: 6 }} type="number" step="0.01" size="large" placeholder={translations[language]?.kmRatePlaceholder} min={0} />
                </Form.Item>
              </div>

              <Form.Item label={translations[language]?.TravelExpenses} name="travelExpenses">
                <Input style={{ borderRadius: 6 }} type="number" step="0.01" size="large" placeholder={translations[language]?.amount} min={0} />
              </Form.Item>
            </div>

            <div className="button-main-div" style={{ display: 'flex', gap: 8 }}>
              <Button size="large" onClick={handleCancel} className="cancel-button" disabled={submitting}>
                {translations[language]?.cancel}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                className="submit-button"
                icon={isEdit ? <Edit size={20} /> : <Plus size={20} />}
                loading={submitting}
              >
                {isEdit
                  ? translations[language]?.updateLog
                  : translations[language]?.AddToLog}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  )
}

export default LogEntry
